import * as React from 'react'
import {
  TextField as MaterialTextField,
  SxProps,
  TextFieldProps,
  IconButton,
} from '@design/pt-ui'
import { Clear as ClearIcon } from '@mui/icons-material'

import { useDidUpdate } from '../utils/hooks'
import { stripSpecialCharacters } from '../utils/string'

type Props = {
  text?: TextFieldProps
  value: string
  locked?: boolean
  debounce?: number
  onPendingChange?: (pending: boolean) => any
  onChange: (value: string) => any
  onLengthChange?: (len: number) => any
  onKeyPress?: (event: React.KeyboardEvent) => any
  onBlur?: () => any
  sx?: SxProps
  id?: string
}

function TextField(props: Props) {
  const timeout = React.useRef<any>(null)
  const updater = React.useRef<Function>(() => {})

  const [value, setValue] = React.useState<string>(props.value || '')

  const onChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (props.locked) {
        return
      }

      const value = stripSpecialCharacters(event.target.value)
      setValue(value)

      if (timeout.current) {
        clearTimeout(timeout.current)
      }

      updater.current = () => {
        if (props.onPendingChange) {
          props.onPendingChange(false)
        }

        if (props.onChange) {
          props.onChange(value)
        }
      }

      if (props.onPendingChange) {
        props.onPendingChange(true)
      }

      timeout.current = setTimeout(() => {
        updater.current()
        updater.current = () => {}
      }, props.debounce || 500)
    },
    [props.locked, props.onChange, props.onPendingChange], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const onBlur = React.useCallback((_event: React.SyntheticEvent) => {
    if (timeout.current) {
      clearTimeout(timeout.current)
    }

    updater.current()
    updater.current = () => {}
  }, [])

  useDidUpdate(() => {
    if (props.value === value) {
      return
    }

    if (timeout.current) {
      clearTimeout(timeout.current)
    }

    setValue(props.value)

    if (props.onPendingChange) {
      props.onPendingChange(false)
    }

    if (props.onChange) {
      props.onChange(props.value)
    }
  }, [props.value])

  React.useEffect(() => {
    if (props.onLengthChange) {
      props.onLengthChange(value.length)
    }
  }, [value.length]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MaterialTextField
      {...(props.text || {})}
      onChange={onChange}
      onKeyPress={props.onKeyPress}
      onBlur={onBlur}
      value={value}
      id={props.id}
      variant={props.text?.variant ?? 'standard'}
      sx={{ mb: 0, ...props.sx, width: '100%' }}
      InputProps={{
        endAdornment: props.text?.type === 'search' && value.length > 0 && (
          <IconButton
            onClick={() => {
              setValue('')
              props.onChange('')
            }}>
            <ClearIcon />
          </IconButton>
        ),
      }}
    />
  )
}

export default TextField
